// Sign-in to the admin console (D38, D56): the hosted page through GET /v1/admin/auth/sign-in,
// which needs MFA and comes back with the admin session. Locally, with the dev identity
// provider, a form signs in as any email and simulates MFA (POST /v1/admin/auth/dev/session);
// it is in `vite dev` and in builds with VITE_DEV_SIGN_IN=true (the end-to-end suite), never in
// a production build. Only staff get past /me; anyone else sees why here.

import { EntryHead, EntryNote, EntryPage } from '@apsio/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useId, useState } from 'react'
import { API_BASE, isApiError, safeReturnTo } from '../api/client'
import { devSignIn, meQuery } from '../api/queries'
import { errorMessage } from '../components/states'
import { useI18n } from '../i18n'
import { StepUp } from './StepUp'

const DEV_SIGN_IN = import.meta.env.DEV || import.meta.env.VITE_DEV_SIGN_IN === 'true'

export function SignInPage() {
  const { t } = useI18n()
  const search = useSearch({ strict: false }) as { return_to?: string }
  const returnTo = safeReturnTo(search.return_to) ?? '/'
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [mfa, setMfa] = useState(true)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const emailId = useId()
  const nameId = useId()
  const mfaId = useId()
  const me = useQuery({ ...meQuery, throwOnError: false })

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFailed(false)
    try {
      await devSignIn(email.trim(), name.trim(), mfa)
      await qc.resetQueries({ queryKey: meQuery.queryKey })
      await navigate({ to: returnTo })
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  if (me.data && !busy) return <Navigate to={returnTo} />
  // Signed in without a passkey: the authenticator app's code first (StepUp).
  const stepUp = isApiError(me.error, 403) && me.error.code === 'mfa_required'
  // Signed in, but refused: not staff, or a session past 12 hours.
  const refused = me.error && !isApiError(me.error, 401) && !stepUp
  return (
    <EntryPage tag={t('shell.badge')}>
      <EntryHead title={t('signIn.title')} lede={t('signIn.lede')} />
      {refused ? <EntryNote tone="crash">{errorMessage(t, me.error)}</EntryNote> : null}
      {stepUp ? (
        <StepUp
          onDone={async () => {
            await qc.resetQueries({ queryKey: meQuery.queryKey })
            await navigate({ to: returnTo })
          }}
        />
      ) : (
        <div className="en-acts">
          <a className="btn solid" href={`${API_BASE}/auth/sign-in`}>
            {t('signIn.continue')}
          </a>
        </div>
      )}
      {DEV_SIGN_IN ? (
        <form className="en-win en-dev" onSubmit={submit} aria-label={t('signIn.devForm')}>
          <p className="en-dev-k">{t('signIn.devDivider')}</p>
          <div className="en-lbl">
            <label htmlFor={emailId}>{t('signIn.email')}</label>
            <input
              id={emailId}
              className="input en-in"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="en-lbl">
            <label htmlFor={nameId}>{t('signIn.name')}</label>
            <input
              id={nameId}
              className="input en-in"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <label className="check" htmlFor={mfaId}>
            <input
              id={mfaId}
              type="checkbox"
              checked={mfa}
              onChange={(e) => setMfa(e.target.checked)}
            />
            <span>{t('signIn.devMfa')}</span>
          </label>
          {failed ? <EntryNote tone="crash">{t('signIn.devFailed')}</EntryNote> : null}
          <button type="submit" className="btn solid" disabled={busy}>
            {t('signIn.devSubmit')}
          </button>
          <p className="en-hint">{t('signIn.devNote')}</p>
        </form>
      ) : null}
    </EntryPage>
  )
}
